import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, repeatsAClass } from '../skillUsage';

export const orbitTextRingSkill: CodeoSkill = {
    name: SKILL_NAMES.orbitTextRing,
    kind: 'codeo',
    description:
        'Wrap a repeated word around a tilted ring that spins in depth — letters swinging round ' +
        'the front large and readable and round the back small, mirrored and faint — for a word ' +
        'about loops, cycles, orbiting, repeating forever, or "always".',
    checkLooksFor:
        'letters placed round a ring with Math.sin and Math.cos of an angle, re-placed from an onUpdate',
    documentUsesSkill: check((script, markup) =>
        repeatsAClass(markup, 'span') || /createElement\(['"]span/.test(script)
            ? /Math\.sin/.test(script) && /Math\.cos/.test(script) && /onUpdate/.test(script)
            : false,
    ),
    reviewNote:
        'Letters drawn narrow, mirrored or faint toward the edges and the back of the ring are this ' +
        'technique working: they are the far side of a ring seen in depth.',
    instructions: `# Orbit Text Ring

The word, repeated with a separator ("LOOP • LOOP • LOOP • "), runs **all the way round a ring** that is tilted toward the viewer, and the ring **spins**. Letters swing across the front large, upright and readable, narrow to slivers as they turn edge-on at the sides, and come round the back **small, faint and mirrored** — the back of the ring seen through itself. Two rings, one above the other in contrasting colours and spinning at different speeds, make it read as a loop that never ends.

Use it for loops, cycles, orbits, repetition, routines, "always", "24/7", "around".

The ring is projected in 2D by script: each letter is placed every frame from its angle on the ring. No CSS 3D is involved, so it renders the same everywhere.

## Markup

Empty ring holders; the script fills them.

\`\`\`html
<div id="ring-a" class="ring absolute inset-0" data-l5-allow-literal></div>
<div id="ring-b" class="ring absolute inset-0" data-l5-allow-literal aria-hidden="true"></div>
\`\`\`

\`\`\`css
.ring span { position: absolute; left: 0; top: 0; font-family: var(--l5-font-0), sans-serif; font-weight: 800; text-transform: uppercase; line-height: 1; white-space: pre; }
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const SCENE = 2.0;

function buildRing(holder, text, color, size) {
  const letters = [];
  const probe = document.createElement('span');
  probe.style.fontSize = size + 'px';
  holder.appendChild(probe);
  let total = 0;
  const widths = [...text].map((c) => { probe.textContent = c; const w = probe.offsetWidth; total += w; return w; });
  probe.remove();
  let acc = 0;
  [...text].forEach((c, i) => {
    const s = document.createElement('span');
    s.textContent = c;
    s.style.fontSize = size + 'px';
    s.style.color = color;
    holder.appendChild(s);
    letters.push({ el: s, phi: ((acc + widths[i] / 2) / total) * Math.PI * 2, w: widths[i], h: s.offsetHeight });
    acc += widths[i];
  });
  return letters;
}

// The ring's diameter is set first — about 80% of the frame's width — and the type is sized so the
// repeated text runs exactly once round it, letters edge to edge. Every letter is measured, so the
// rings are built again once the video's font has loaded and the glyphs have their real widths.
const DIAMETER = 1920 * 0.8, TEXT = 'LOOP • '.repeat(6);
const holderA = document.querySelector('#ring-a'), holderB = document.querySelector('#ring-b');
const circumference = (ring) => ring.reduce((s, l) => s + l.w, 0);
let ringA = [], ringB = [];
function buildRings() {
  holderA.innerHTML = '';
  holderB.innerHTML = '';
  const trial = buildRing(holderA, TEXT, 'transparent', 100);
  const SIZE = 100 * (Math.PI * DIAMETER) / circumference(trial);
  holderA.innerHTML = '';
  ringA = buildRing(holderA, TEXT, 'var(--l5-color-0, #111)', SIZE);
  ringB = buildRing(holderB, TEXT, 'var(--l5-color-3, #f2f2f2)', SIZE);
}
buildRings();

function place(ring, cx, cy, tilt, angle) {
  const R = circumference(ring) / (Math.PI * 2);
  for (const l of ring) {
    const a = l.phi + angle;
    const depth = Math.cos(a);                     // 1 front, -1 back
    const x = cx + R * Math.sin(a);
    const y = cy + R * tilt * depth;               // the ring tilts toward us
    const persp = 1 + 0.18 * depth;                // nearer is bigger
    const sx = Math.cos(a) * persp;                // edge-on at the sides, mirrored at the back
    l.el.style.transform = 'translate(' + (x - l.w / 2) + 'px,' + (y - l.h / 2) + 'px) scale(' + sx.toFixed(3) + ',' + persp.toFixed(3) + ')';
    l.el.style.opacity = depth > 0.05 ? 1 : 0.22;
    l.el.style.zIndex = depth > 0 ? 2 : 1;
  }
}

const state = { a: 0 };
const draw = () => {
  // Each ring is placed by where its front runs, so the two fronts are a clear band apart and the
  // lower ring's back passes above its own front, not through the upper ring's.
  const R = circumference(ringA) / (Math.PI * 2), TILT = 0.18;
  place(ringA, 960, 380 - R * TILT, TILT, state.a);
  place(ringB, 960, 880 - R * TILT, TILT, -0.6 + state.a * 0.8);
};
// A whip that is still coasting when the scene ends: fast in, easing out.
tl.fromTo(state, { a: 1.6 }, { a: -0.4, duration: SCENE * 1.4, ease: 'power1.out', onUpdate: draw }, 0);
document.fonts.ready.then(() => { buildRings(); draw(); });
tl.set({}, {}, SCENE);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Size**: the ring is about 80% of the frame's width, and the type size follows from it. More repetitions make smaller letters on the same ring; five to seven keep the front word large.
- **One ring** for a quieter loop; two counter-coloured rings for the full effect.
- **Direction**: the front of the ring moves the way the reading goes (right to left across the front reads the word forward). Flip the sign of the angle to reverse it.

## Pitfalls

- **Placed every frame.** All the placing happens inside the \`onUpdate\`, from \`state.a\` and the current rings; nothing is placed once and left.
- **Measured in the real font.** The letter widths and the type size come from the glyphs, so they are measured again when \`document.fonts.ready\` resolves. Measured only in the fallback face, the letters overlap or leave gaps once the video's font arrives.
- **Front and back apart, rings apart.** A ring's back passes above its front by twice the radius times the tilt, so place each ring by where its *front* runs, and keep the two fronts about half the frame's height apart: the lower ring's back then runs between the two fronts instead of through the upper one. A tilt of 0.15–0.2 at this size; much less and a ring's back prints over its own front.
- **The back is faint.** At full opacity the back of the ring tangles with the front and neither reads.
- **Front letters readable.** The front arc is where the word is read, so it must be wide enough to show the word whole: if it shows only half the word, use fewer repetitions (a smaller ring) or a smaller tilt.
- The letters carry no CSS \`transform\`; the script owns it.`,
};
